import React, { useCallback } from 'react'
import maplibregl from 'maplibre-gl'
import buffer from '@turf/buffer'
import MapLibreMap, { addGeoJsonLayer, fitBoundsToGeoJson } from './MapLibreMap'

// Color scheme matching polo_map.js
const COLORS = {
  polo: '#EF7A7A',
  lotes: '#81ADD2',
  quadras: '#81ADD2',
  quadraLotes: '#555',
  surroundingQuadras: '#ddd'
}

export default function PoloMapLibre({ poloId, className = '' }) {
  const handleMapReady = useCallback(async (map) => {
    try {
      // Fetch all data in parallel
      const [polo, quadras, lotes, quadraLotes, surroundingQuadras] = await Promise.all([
        fetch(`/polos/${poloId}.json`).then(r => r.json()),
        fetch(`/polos/${poloId}/quadras`).then(r => r.json()),
        fetch(`/polos/${poloId}/lotes`).then(r => r.json()),
        fetch(`/polos/${poloId}/quadra_lotes`).then(r => r.json()),
        fetch(`/polos/${poloId}/surrounding_quadras`).then(r => r.json())
      ])

      // Add surrounding quadras (background, lowest layer)
      if (surroundingQuadras?.features?.length > 0) {
        addGeoJsonLayer(map, 'surrounding-quadras', surroundingQuadras, {
          type: 'both',
          color: COLORS.surroundingQuadras,
          opacity: 0.1,
          lineWidth: 1,
          onClick: (e) => {
            const id = e.features[0]?.properties?.id
            if (id) window.location.href = `/quadras/${id}`
          }
        })
      }

      // Add quadra lotes (lotes from quadras but outside polo)
      if (quadraLotes?.features?.length > 0) {
        addGeoJsonLayer(map, 'quadra-lotes', quadraLotes, {
          type: 'both',
          color: COLORS.quadraLotes,
          opacity: 0.2,
          lineWidth: 0.5,
          onClick: (e) => {
            const id = e.features[0]?.id
            if (id) window.location.href = `/lotes/${id}`
          }
        })
      }

      // Add quadras with buffer (4 meters)
      if (quadras?.features?.length > 0) {
        const bufferedQuadras = buffer(quadras, 4, { units: 'meters' })
        addGeoJsonLayer(map, 'quadras', bufferedQuadras, {
          type: 'both',
          color: COLORS.quadras,
          opacity: 0.1,
          lineWidth: 1,
          onClick: (e) => {
            const id = e.features[0]?.properties?.id
            if (id) window.location.href = `/quadras/${id}`
          }
        })

        // Fit bounds to quadras
        fitBoundsToGeoJson(map, quadras)
      }

      // Add polo lotes (lotes inside the polo)
      if (lotes?.features?.length > 0) {
        addGeoJsonLayer(map, 'lotes', lotes, {
          type: 'both',
          color: COLORS.lotes,
          opacity: 0.5,
          lineWidth: 1,
          onClick: (e) => {
            const id = e.features[0]?.id
            if (id) window.location.href = `/lotes/${id}`
          }
        })
      }

      // Add polo boundary (top layer)
      if (polo) {
        addGeoJsonLayer(map, 'polo', polo, {
          type: 'both',
          color: COLORS.polo,
          opacity: 0.3,
          lineWidth: 2
        })
      }

    } catch (error) {
      console.error('Error loading polo map data:', error)
    }
  }, [poloId])

  return (
    <MapLibreMap
      onMapReady={handleMapReady}
      className={className}
      zoom={14}
    />
  )
}
